import { useState } from "react";
import { Field } from "./Field";
import { api, type ItemDetail } from "../lib/api";
import { MEASUREMENT_FIELDS, garmentOf, measurementsRecord, type Garment } from "../lib/measurements";

const LABEL: Record<string, { label: string; hint?: string }> = {
  pit_to_pit: { label: "Pit to pit", hint: "Armpit to armpit, laid flat" },
  sleeve: { label: "Sleeve", hint: "Shoulder seam to cuff" },
  shoulder: { label: "Shoulder", hint: "Seam to seam across the back" },
  waist: { label: "Waist", hint: "Across the top, laid flat" },
  inseam: { label: "Inseam", hint: "Crotch seam to hem" },
  rise: { label: "Rise", hint: "Crotch seam to top of waistband" },
  hip: { label: "Hip", hint: "Widest point, laid flat" },
  leg_opening: { label: "Leg opening", hint: "Across the hem, laid flat" },
};
const LENGTH_HINT: Record<Garment, string> = {
  top: "Top of the shoulder to the hem",
  bottom: "Top of the waistband to the hem",
};

/**
 * The "Measurements" panel on an item. The category decides whether it asks
 * for top or bottoms measurements; when it can't tell (a dress, a romper),
 * both choices are offered. Inches, laid flat.
 */
export function MeasurementsPanel({ item, onClose, onSaved }: {
  item: ItemDetail;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const stored = item.measurements ?? {};
  const guessed = garmentOf(item.category, item.subcategory);
  const [garment, setGarment] = useState<Garment>(
    (stored.garment === "top" || stored.garment === "bottom" ? stored.garment : guessed) ?? "top");
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(stored).filter(([, v]) => typeof v === "number").map(([k, v]) => [k, String(v)])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const nums = Object.fromEntries(MEASUREMENT_FIELDS[garment].map((k) => {
        const raw = (values[k] ?? "").trim();
        return [k, raw === "" ? null : Number(raw)];
      }));
      await api.patch(`/items/${item.id}`, { measurements: measurementsRecord(garment, "in", nums) });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the measurements");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rail">
      <h2 className="eyebrow rail-label">Measurements — inches, laid flat</h2>
      {guessed === null && (
        <div className="actions" role="radiogroup" aria-label="Measure as">
          {(["top", "bottom"] as const).map((g) => (
            <button key={g} role="radio" aria-checked={garment === g}
              className={garment === g ? undefined : "ghost"} onClick={() => setGarment(g)} disabled={busy}>
              {g === "top" ? "Top" : "Bottoms"}
            </button>
          ))}
        </div>
      )}
      <div className="row">
        {MEASUREMENT_FIELDS[garment].map((k) => (
          <Field key={k} label={k === "length" ? "Length" : LABEL[k]!.label} hint={k === "length" ? LENGTH_HINT[garment] : LABEL[k]!.hint}>
            <input
              type="number" inputMode="decimal" min="0" step="0.25"
              value={values[k] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
      {error && <div className="notice" style={{ marginTop: "0.75rem" }}>{error}</div>}
      <div className="actions">
        <button onClick={() => void save()} disabled={busy}>{busy ? "Saving" : "Save measurements"}</button>
        <button className="quiet" onClick={onClose} disabled={busy}>Close</button>
      </div>
    </section>
  );
}
